import { useState } from 'react';
import { ClickAwayListener } from '@mui/material';
import { noop } from 'lodash';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { clsx } from 'clsx';
import {
  dropDownWrapper,
  dropDownList,
  visible,
  componentWrapper,
  dropDownControl,
} from './dropDownMultiselect.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SearchInput } from '@design-system/components/searchinput';
import { Checkbox } from '@design-system/components/checkbox/checkbox';
import { Typography } from '@design-system/components/typography/typography';
import { sprinkles } from '@design-system/sprinkles.css';

type Option<T> = {
  name: string;
  id: T;
  disabled?: boolean;
};

export type DropDownMultiselectProps<T> = {
  items: Array<Option<T>>;
  menuClassName?: string;
  disabled?: boolean;
  value: T[];
  onChange: (value: T[]) => void;
  toFormOnChange: (value: T, selectedValues: T[]) => T[];
};

export const DropDownMultiselect = <T extends string | number>(
  p: DropDownMultiselectProps<T>,
) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const options = p.items.filter((o) =>
    o.name
      .toString()
      .toLocaleLowerCase()
      .includes(searchText.toLocaleLowerCase()),
  );

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div
        className={clsx(
          componentWrapper,
          p.disabled && sprinkles({ color: 'N-60' }),
        )}
      >
        <div
          className={dropDownControl}
          onClick={() => !p.disabled && setOpen((prev) => !prev)}
        >
          <Typography variant="body" size="large">
            Select
          </Typography>
          <FontAwesomeIcon icon={faCaretDown} />
        </div>
        <div
          className={clsx(open && visible, dropDownWrapper, p?.menuClassName)}
        >
          <SearchInput
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onClear={() => setSearchText('')}
            disabled={!!p.disabled}
          />
          <div className={dropDownList}>
            {options.map(({ name, id, disabled }) => (
              <div
                key={id}
                className={clsx('p-2', disabled && 'pointer-events-none')}
                onClick={() => {
                  const newValues = p.toFormOnChange(id, p.value || []);
                  p.onChange(newValues);
                }}
              >
                <Checkbox
                  label={name}
                  onChange={noop}
                  checked={(p.value || []).includes(id)}
                  textVariant="body"
                  textSize="large"
                  checkboxVariant="neutral"
                  disabled={disabled}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </ClickAwayListener>
  );
};
